export type ProveFlowType = 'desktop' | 'mobile';
export type ProveFallbackAction = 'continue' | 'email';
export type ProveFallbackReason = 'NoProveMatch' | 'PhoneMismatch' | 'ProveUnavailable';
export type ProveAuthFallbackReason = 'NoProveMatch' | 'ProveUnavailable';
export type ProveAuthMethod = 'emailOTP' | 'instantLink' | 'phoneOTP';

export type ProveAuthAction =
  | 'ChallengeSent'
  | 'Completed'
  | 'ContinueAfterFallback'
  | 'Failed'
  | 'FallbackShown'
  | 'NoProveMatch'
  | 'Requested'
  | 'Required'
  | 'Sent'
  | 'ResendRequested'
  | 'Submit'
  | 'WrongCodeSubmitted';

type ProveCustomerType = 'Existing' | 'New';

/**
 * PROVE_STEP funnel (XF-10207). Step-shown is free (Wizard FirstStepLoaded / StepChange); these are
 * the interactions inside the step that the wizard cannot see.
 */
export type ProveStepAction =
  | 'ContinueClicked'       // applicant submitted the PROVE_STEP form
  | 'Started'               // prove/start succeeded and the SDK was handed the auth token
  | 'Verified'              // prove/complete succeeded
  | 'ConsentRecorded'       // provePrefillConsent written (same call as Verified; separate funnel event)
  | 'Failed'                // start, SDK or complete failed — fallback shown
  | 'Declined'              // applicant continued manually from the fallback
  | 'EnterManuallyClicked'; // applicant skipped Prove from the form

export interface ProveStepEventInput {
  action: ProveStepAction;
  bucket?: string;
  channelCode?: string;
  flowType?: ProveFlowType;
  properties?: Record<string, unknown>;
}

export interface ProveAnalyticsEvent {
  event: 'ProveAuthEvent' | 'ProvePrefillFallback' | 'ProveStepEvent';
  properties: Record<string, unknown>;
}

export const toProveCustomerType = (associate?: boolean): ProveCustomerType => {
  return associate === true ? 'Existing' : 'New';
};

export const getProveFallbackReason = (error?: string): ProveFallbackReason | undefined => {
  const normalized = error?.toLowerCase();
  if (!normalized) return undefined;
  if (normalized.includes('phone')) return 'PhoneMismatch';
  if ((normalized.includes('match') && normalized.includes('not')) || normalized.includes('not found') || normalized.includes('no data')) return 'NoProveMatch';

  return 'ProveUnavailable';
};

export const getProveStartFallbackReason = (error?: string): ProveFallbackReason | undefined => {
  const fallbackReason = getProveFallbackReason(error);
  if (!fallbackReason) return undefined;

  return fallbackReason === 'NoProveMatch' ? 'PhoneMismatch' : fallbackReason;
};

export const buildProvePrefillFallbackEvent = ({ fallbackAction, fallbackReason, flowType, associate }: { fallbackAction: ProveFallbackAction; fallbackReason?: ProveFallbackReason; flowType: ProveFlowType; associate?: boolean }): ProveAnalyticsEvent => ({
  event: 'ProvePrefillFallback',
  properties: {
    customer: toProveCustomerType(associate),
    fallbackAction: fallbackAction === 'email' ? 'Email' : 'Continue',
    ...(fallbackReason ? { fallbackReason } : {}),
  },
});

export const toProveAuthMethod = (flowType: ProveFlowType): ProveAuthMethod => {
  return flowType === 'mobile' ? 'phoneOTP' : 'instantLink';
};

export const buildProveStepEvent = ({ action, bucket, channelCode, flowType, properties = {} }: ProveStepEventInput): ProveAnalyticsEvent => ({
  event: 'ProveStepEvent',
  properties: {
    action,
    surface: 'PROVE_STEP',
    ...(bucket ? { bucket } : {}),
    ...(channelCode ? { channelCode } : {}),
    ...(flowType ? { flowType, method: toProveAuthMethod(flowType) } : {}),
    ...properties,
  },
});

export const buildProveAuthEvent = ({ action, associate, flowType, method, properties = {} }: { action: ProveAuthAction; associate?: boolean; flowType: ProveFlowType; method?: ProveAuthMethod; properties?: Record<string, unknown> }): ProveAnalyticsEvent => ({
  event: 'ProveAuthEvent',
  properties: {
    action,
    customer: toProveCustomerType(associate),
    method: method ?? toProveAuthMethod(flowType),
    ...properties,
  },
});